/**
 * 历史牌局的两个只读端点：列表（`GET /me/hands`）与单手详情（`GET /hands/:handId`）。
 *
 * 服务端早就实现并测过（`HandRepo.listAccountHands` / `getReplay`），客户端一直没接线——
 * 和 `PATCH /me` 是同一种情况：能力在，入口没人搭。
 *
 * `HandReplayDTO` 里 `steps`（每步之后的完整引擎快照，用于逐帧回放）在这里刻意不接：
 * 「查看历史操作记录」要的是一份读得懂的动作日志，不是一个要另做播放器的回放器；
 * `actions` 已经是完整的动作序列，配 `board`/`seats` 足够看懂整手牌怎么打的。
 * 少接一个字段，类型定义也不用陪着服务端引擎的内部快照结构一起演进。
 */
import type {
  AccountId,
  AnteMode,
  Card,
  Chips,
  HandId,
  HoleCardsView,
  PublicAction,
  RoomId,
  SeatIndex,
  SeatOutcome,
  Street,
} from '@poker/shared'
import type { HandSummary } from '@poker/shared'

import { getAuthSession } from '../lobby/useAuth.js'
import { AuthError, parseJsonBody, readApiError, type AuthSession } from '../auth/session.js'

export type HistoryFailureKind = 'network' | 'not-authenticated' | 'not-found' | 'server'

export class HistoryError extends Error {
  constructor(
    override readonly message: string,
    readonly kind: HistoryFailureKind,
  ) {
    super(message)
    this.name = 'HistoryError'
  }
}

export interface HistoryApiOptions {
  readonly auth?: AuthSession
  readonly fetchImpl?: typeof fetch
}

// ============================================================================
// 单手详情：本地类型描述响应体的形状（服务端类型是 db/repos 内部类型，不对外共享）
// ============================================================================

export interface ReplaySeat {
  readonly seat: SeatIndex
  readonly accountId: AccountId
  readonly nickname: string
  readonly isBot: boolean
  readonly position: string
  readonly startingStack: Chips
  readonly endingStack: Chips
  readonly holeCards: HoleCardsView
  readonly contributed: Chips
  readonly uncalledReturned: Chips
  readonly won: Chips
  readonly net: number
  readonly outcome: SeatOutcome | null
  readonly wentToShowdown: boolean
}

export interface ReplayHead {
  readonly id: HandId
  readonly roomId: RoomId
  readonly handNo: number
  readonly status: string
  readonly buttonSeat: SeatIndex
  readonly sbSeat: SeatIndex | null
  readonly bbSeat: SeatIndex
  readonly sbAmount: Chips
  readonly bbAmount: Chips
  readonly anteMode: AnteMode
  readonly ante: Chips
  readonly board: readonly Card[]
  readonly finalStreet: Street | null
  readonly endedBy: 'fold' | 'showdown' | null
}

export interface HandReplay {
  readonly hand: ReplayHead
  readonly seats: readonly ReplaySeat[]
  readonly actions: readonly PublicAction[]
}

// ============================================================================
// 错误 → 人话
// ============================================================================

function describeHistoryFailure(kind: HistoryFailureKind): string {
  switch (kind) {
    case 'network':
      return '网络没连上，检查一下再试'
    case 'not-authenticated':
      return '本机的登录状态过期了，刷新一下页面再来'
    case 'not-found':
      return '这手牌不存在，或者你没有权限查看'
    default:
      return '服务器出了点问题，再试一次'
  }
}

function classify(err: AuthError): HistoryFailureKind {
  if (err.code === 'E_NETWORK' || err.status === 0) return 'network'
  if (err.status === 401) return 'not-authenticated'
  if (err.status === 404) return 'not-found'
  return 'server'
}

function toHistoryError(e: unknown): HistoryError {
  if (e instanceof HistoryError) return e
  const err = e instanceof AuthError ? e : new AuthError(String(e), 'E_NETWORK', 0, false)
  const kind = classify(err)
  return new HistoryError(describeHistoryFailure(kind), kind)
}

// ============================================================================
// 传输
// ============================================================================

async function get(auth: AuthSession, fetchImpl: typeof fetch, path: string): Promise<unknown> {
  const token = await auth.ensureAccessToken()
  let res: Response
  try {
    res = await fetchImpl(`${auth.baseUrl()}/api${path}`, {
      headers: { authorization: `Bearer ${token}` },
    })
  } catch (e) {
    throw new AuthError(`网络不可用：${String(e)}`, 'E_NETWORK', 0, false)
  }
  const parsed = parseJsonBody(await res.text())
  if (!res.ok) throw readApiError(res.status, parsed)
  return parsed
}

function resolve(options: HistoryApiOptions): { auth: AuthSession; fetchImpl: typeof fetch } {
  return {
    auth: options.auth ?? getAuthSession(),
    fetchImpl: options.fetchImpl ?? ((...args) => globalThis.fetch(...args)),
  }
}

/** 每页条数：够看最近一段时间的手牌，又不至于一次拖回一份很大的列表。 */
export const HISTORY_PAGE_SIZE = 20

/** 列出我打过的手牌，最新的在前。`before` 传上一页最后一条的 handId 翻下一页。 */
export async function listMyHands(before?: string, options: HistoryApiOptions = {}): Promise<HandSummary[]> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const qs = new URLSearchParams({ limit: String(HISTORY_PAGE_SIZE) })
    if (before !== undefined) qs.set('before', before)
    const body = (await get(auth, fetchImpl, `/me/hands?${qs.toString()}`)) as { hands?: HandSummary[] } | null
    return body?.hands ?? []
  } catch (e) {
    throw toHistoryError(e)
  }
}

/** 单手详情：board / 每个座位的结果 / 完整动作日志。 */
export async function getHandReplay(handId: string, options: HistoryApiOptions = {}): Promise<HandReplay> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = (await get(auth, fetchImpl, `/hands/${encodeURIComponent(handId)}`)) as Partial<HandReplay> | null
    if (body?.hand == null || body.seats == null || body.actions == null) {
      throw new HistoryError(describeHistoryFailure('server'), 'server')
    }
    return body as HandReplay
  } catch (e) {
    throw toHistoryError(e)
  }
}
